import i18n from "i18next";
import PropTypes from "prop-types";
import React, { useCallback, useMemo, useState } from "react";

import IdealStudio from "../../../../core/editor/main";
import { HiDotsVertical } from "react-icons/hi";
import { styled } from "@mui/material/styles";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Menu from "@mui/material/Menu";
import CircularProgress from "@mui/material/CircularProgress";

import { Dropdown } from "react-bootstrap";

import Flag from "react-flagkit";
import {
  HiOutlineCloudUpload,
  HiOutlineCog,
  HiOutlineQuestionMarkCircle,
  HiOutlineLogout,
} from "react-icons/hi";
import {
  LuImport,
  LuLanguages,
  LuCopy,
  LuPlus,
  LuVideo,
  LuProportions,
  LuCaptions,
  LuReceipt,
} from "react-icons/lu";

/**
 * Dropdown menu in the editor's navbar
 */
const NavDropdown = (props) => {
  const {
    lmsExporting,
    isLmsConnect,
    exportToLMS,
    toggleFileUpload,
    toggleExport,
    toggleVideoExport,
    toggleSubtitles,
    toggleActivityLog,
    toggleGlobalConfig,
    openTour,
    openExitModal,
    saveAs,
    openRatioReproduce,
    openNewTraining,
    onLeftPanelChange,
  } = props;

  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);

  const StyledMenu = useMemo(
    () =>
      styled(Menu)(() => ({
        "& .MuiPaper-root": {
          border: "1px solid #e2e8f0",
          width: 260,
          borderRadius: 12,
          boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
          padding: "6px 0",
        },
      })),
    []
  );

  const rowStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: "10px 14px",
    color: "#334155",
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
    borderRadius: 10,
    transition: "background 0.15s ease",
  };

  const iconStyle = { color: "#6b7280", fontSize: 18, display: "flex", alignItems: "center" };

  const MenuRow = ({ icon, label, onClick, disabled }) => (
    <div
      role="button"
      tabIndex={0}
      onClick={() => {
        if (disabled) return;
        onClick();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !disabled) onClick();
      }}
      style={{
        ...rowStyle,
        opacity: disabled ? 0.5 : 1,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#f8fafc";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={iconStyle}>{icon}</span>
        <span>{label}</span>
      </div>
    </div>
  );

  const handleLanguageChange = useCallback((lang) => {
    // 1) First load plugin resources for the new language.
    //    We explicitly pass `lang` because `i18n.language` hasn't updated yet.
    IdealStudio.Plugins.loadAll(lang);

    // 2) Now change react-i18next language which triggers all components
    //    (including questions) to re-render using the newly loaded resources.
    i18n.changeLanguage(lang);

    // Small trick: emit languageChanged manually to ensure all React bindings resync
    if (typeof i18n.emit === "function") {
      i18n.emit("languageChanged", lang);
    }

    const collapseEl = document.getElementById("scontainedViewsCollapse");
    const menuEl = document.getElementsByClassName("pluginsMenu")[0];
    if (collapseEl) collapseEl.click();
    if (menuEl) menuEl.click();
    setOpen(false);
  }, []);

  return (
    <>
      <div className="userControls">
        <Dropdown.Toggle
          onClick={(event) => {
            setAnchorEl(event.currentTarget);
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <HiDotsVertical />
          </div>
        </Dropdown.Toggle>
        <StyledMenu
          id="customized-menu"
          anchorEl={anchorEl}
          style={{
            right: "0px",
          }}
          elevation={0}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={() => {
            setAnchorEl(null);
          }}
        >
          <div style={{ padding: "6px 10px", display: "flex", flexDirection: "column", gap: 4 }}>
            <MenuRow
              icon={<LuPlus />}
              label={i18n.t("nav_dropdown.new_training") || "Yeni Oluştur"}
              onClick={() => {
                setAnchorEl(null);
                if (openNewTraining) openNewTraining();
              }}
            />
            <MenuRow
              icon={<LuCopy />}
              label={i18n.t("save_as") || "Save As"}
              onClick={() => {
                setAnchorEl(null);
                if (saveAs) {
                  saveAs();
                } else {
                  toggleExport();
                }
              }}
            />
            {openRatioReproduce && (
              <MenuRow
                icon={<LuProportions />}
                label={i18n.t("ratio_reproduce.menu_label") || "Farklı Oranda Üret"}
                onClick={() => {
                  setAnchorEl(null);
                  openRatioReproduce();
                }}
              />
            )}
            <MenuRow
              icon={<LuImport />}
              label={i18n.t("import") || "Import"}
              onClick={() => {
                setAnchorEl(null);
                if (onLeftPanelChange) {
                  onLeftPanelChange("library");
                } else {
                  toggleFileUpload(undefined, "*");
                }
              }}
            />
            <MenuRow
              icon={<HiOutlineCloudUpload />}
              label={i18n.t("messages.export") || "Export"}
              onClick={() => {
                setAnchorEl(null);
                toggleExport();
              }}
            />
            {toggleSubtitles && (
              <MenuRow
                icon={<LuCaptions />}
                label={i18n.t("nav_dropdown.subtitles") || "Altyazılar"}
                onClick={() => {
                  setAnchorEl(null);
                  toggleSubtitles();
                }}
              />
            )}
            {toggleVideoExport && (
              <MenuRow
                icon={<LuVideo />}
                label={i18n.t("video_export.menu_label", {
                  defaultValue: "Video Export",
                })}
                onClick={() => {
                  setAnchorEl(null);
                  toggleVideoExport();
                }}
              />
            )}
            {isLmsConnect && (
              <MenuRow
                icon={<HiOutlineCloudUpload />}
                label={i18n.t("export_to_lms") || "Export to LMS"}
                disabled={lmsExporting}
                onClick={() => {
                  setAnchorEl(null);
                  exportToLMS();
                }}
              />
            )}
            <MenuRow
              icon={<HiOutlineCog />}
              label={i18n.t("messages.global_config") || "Global Config"}
              onClick={() => {
                setAnchorEl(null);
                toggleGlobalConfig();
              }}
            />
            {toggleActivityLog && (
              <MenuRow
                icon={<LuReceipt />}
                label={i18n.t("activity_log.menu_label", {
                  defaultValue: "Aktivite Geçmişi",
                })}
                onClick={() => {
                  setAnchorEl(null);
                  toggleActivityLog();
                }}
              />
            )}
            <MenuRow
              icon={<LuLanguages />}
              label={i18n.t("language") || "Language"}
              onClick={() => {
                setOpen(!open);
              }}
            />
            <Collapse in={open} timeout="auto" unmountOnExit>
              <List component="div" style={{ marginLeft: "20px" }}>
                <ListItemButton
                  selected={i18n.language && i18n.language.startsWith("en")}
                  sx={{
                    "&.Mui-selected": { backgroundColor: "#eff6ff" },
                    "&.Mui-selected:hover": { backgroundColor: "#e0f2fe" },
                  }}
                  onClick={() => {
                    handleLanguageChange("en");
                  }}
                >
                  <ListItemIcon>
                    <Flag country="GB" />
                  </ListItemIcon>
                  <ListItemText primary={i18n.t("messages.language_english")} />
                </ListItemButton>
                <ListItemButton
                  selected={i18n.language && i18n.language.startsWith("tr")}
                  sx={{
                    "&.Mui-selected": { backgroundColor: "#eff6ff" },
                    "&.Mui-selected:hover": { backgroundColor: "#e0f2fe" },
                  }}
                  onClick={() => {
                    handleLanguageChange("tr");
                  }}
                >
                  <ListItemIcon>
                    <Flag country="TR" />
                  </ListItemIcon>
                  <ListItemText primary={i18n.t("messages.language_turkish")} />
                </ListItemButton>
              </List>
            </Collapse>
            <div style={{ borderTop: "1px solid #e2e8f0", margin: "6px -4px 0", paddingTop: 6 }}>
              <MenuRow
                icon={<HiOutlineLogout />}
                label={i18n.t("sign_out") || "Sign out"}
                onClick={() => {
                  setAnchorEl(null);
                  openExitModal();
                }}
              />
            </div>
          </div>
        </StyledMenu>
      </div>
    </>
  );
};

export default NavDropdown;

NavDropdown.propTypes = {
  /**
   * Current selected view (by ID)
   */
  navItemSelected: PropTypes.any.isRequired,
  /**
   * Opens an external catalog with all the resources uploaded to the server
   */
  onExternalCatalogToggled: PropTypes.func,
  /**
   * Callback for opening the file upload modal
   */
  toggleFileUpload: PropTypes.func.isRequired,
  /**
   * Load an specific course from the remote server
   */
  opens: PropTypes.func.isRequired,
  /**
   * Popup that indicates whether the import/export to the server was successful or not
   */
  serverModalOpen: PropTypes.func.isRequired,
  /**
   * Shows/Hides the global course configuration modal form
   */
  toggleGlobalConfig: PropTypes.func.isRequired,
  /**
   * Shows/Hides the exportation modal form
   */
  toggleExport: PropTypes.func.isRequired,
  /**
   * Shows/Hides the standalone Video Export modal (videoOnly mode of
   * ExportModal). Optional — when absent the menu item is hidden.
   */
  toggleVideoExport: PropTypes.func,
  /**
   * Shows/Hides the standalone Subtitles modal. Optional — when absent
   * the menu item is hidden.
   */
  toggleSubtitles: PropTypes.func,
  /**
   * Shows/Hides the credit-activity modal for the open training. Optional —
   * the row is hidden when the host does not provide it.
   */
  toggleActivityLog: PropTypes.func,
  /**
   * Whether LMS export is in progress
   */
  lmsExporting: PropTypes.bool,
  saveAs: PropTypes.func,
  /**
   * Opens the "Farklı Oranda Üret" modal (reproduce training at a new ratio).
   */
  openRatioReproduce: PropTypes.func,
  /**
   * Opens the GlobalConfig modal in `newTraining` mode (kebab → "Yeni Oluştur").
   */
  openNewTraining: PropTypes.func,
  /**
   * Enables the "undo" feature
   */
  undoDisabled: PropTypes.bool,
  /**
   * Shows exit modal
   */
  openExitModal: PropTypes.func.isRequired,
  /**
   * Opens Tour Modal
   */
  openTour: PropTypes.func.isRequired,
  /**
   * Indicates if there is a current server operation
   */
  isBusy: PropTypes.any,
  /**
   * Callback to change the active left panel
   */
  onLeftPanelChange: PropTypes.func,
};
